import { createClient } from "../lib/supabase/client";
import { useNavigate } from "react-router";
import { useEffect } from "react";
import { Search } from "lucide-react";

const supabase = createClient();

export default function Auth() {
    const navigate = useNavigate();

    useEffect(() => {
        supabase.auth.getUser().then(({ data }) => {
            if (data.user) navigate("/");
        });
    }, []);

    async function login(provider: "github" | "google") {
        const { error } = await supabase.auth.signInWithOAuth({
            provider: provider,
            options: {
                redirectTo: `${window.location.origin}/`,
            }
        });
        if (error) {
            console.error("Error while signing in:", error);
        }
    }

    return (
        <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-slate-50">
            {/* Animated bright background */}
            <div className="absolute inset-0">
                <div className="absolute top-1/4 -left-32 w-96 h-96 bg-indigo-300/40 rounded-full blur-[128px] animate-glow-pulse" />
                <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-violet-300/30 rounded-full blur-[128px] animate-glow-pulse delay-1000" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-sky-200/40 rounded-full blur-[100px]" />
            </div>

            {/* Auth Card */}
            <div className="relative z-10 w-full max-w-md mx-4 animate-fade-in-up">
                <div className="backdrop-blur-3xl bg-white/70 border border-white/60 rounded-3xl p-10 shadow-[0_8px_40px_rgba(0,0,0,0.04)]">
                    {/* Logo & Branding */}
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 mb-5 shadow-lg shadow-indigo-500/25 animate-float">
                            <Search className="w-8 h-8 text-white" />
                        </div>
                        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Voxmo</h1>
                        <p className="text-slate-500 mt-2 text-sm font-medium">AI-powered search with real-time web results</p>
                    </div>

                    {/* OAuth Buttons */}
                    <div className="space-y-3">
                        <button
                            onClick={() => login("google")}
                            className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-white hover:bg-slate-50 border border-slate-200 hover:border-indigo-200 rounded-xl text-slate-700 hover:text-indigo-600 font-semibold transition-all duration-200 group cursor-pointer active:scale-[0.98] shadow-sm"
                        >
                            <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" width="20" height="20">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                            </svg>
                            <span className="text-sm">Continue with Google</span>
                        </button>

                        <button
                            onClick={() => login("github")}
                            className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-white hover:bg-slate-50 border border-slate-200 hover:border-indigo-200 rounded-xl text-slate-700 hover:text-indigo-600 font-semibold transition-all duration-200 group cursor-pointer active:scale-[0.98] shadow-sm"
                        >
                            <svg className="w-5 h-5 text-slate-700 group-hover:text-indigo-600 shrink-0 transition-colors" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                            </svg>
                            <span className="text-sm">Continue with GitHub</span>
                        </button>
                    </div>

                    {/* Footer */}
                    <p className="text-center text-slate-400 font-medium text-xs mt-8">
                        By continuing, you agree to our Terms of Service
                    </p>
                </div>
            </div>
        </div>
    );
}